/**
 * Sonar System
 * Toast-like notifications built with stable reducer-driven state
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '../primitives/Box';
import { Flex } from '../primitives/Flex';
import { Text } from '../primitives/Text';

const SonarContext = createContext(null);

const positionStyles = {
  top: { top: '1rem', left: '50%', transform: 'translateX(-50%)' },
  'top-left': { top: '1rem', left: '1rem' },
  'top-right': { top: '1rem', right: '1rem' },
  bottom: { bottom: '1rem', left: '50%', transform: 'translateX(-50%)' },
  'bottom-left': { bottom: '1rem', left: '1rem' },
  'bottom-right': { bottom: '1rem', right: '1rem' },
};

const statusColors = {
  success: {
    bg: 'green.500',
    icon: '✓',
    softBg: 'green.50',
    softBorder: 'green.200',
    text: 'green.800',
    subtleText: 'green.700',
    iconBg: 'green.100',
    solidText: 'white',
  },
  error: {
    bg: 'red.500',
    icon: '✕',
    softBg: 'red.50',
    softBorder: 'red.200',
    text: 'red.800',
    subtleText: 'red.700',
    iconBg: 'red.100',
    solidText: 'white',
  },
  warning: {
    bg: 'orange.500',
    icon: '⚠',
    softBg: 'orange.50',
    softBorder: 'orange.200',
    text: 'orange.800',
    subtleText: 'orange.700',
    iconBg: 'orange.100',
    solidText: 'white',
  },
  info: {
    bg: 'blue.500',
    icon: 'ℹ',
    softBg: 'blue.50',
    softBorder: 'blue.200',
    text: 'blue.800',
    subtleText: 'blue.700',
    iconBg: 'blue.100',
    solidText: 'white',
  },
};

const defaultToastOptions = {
  position: 'top-right',
  duration: 5000,
  isClosable: true,
  status: 'info',
  variant: 'soft',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'ADD':
      return [...state, action.toast];
    case 'REMOVE':
      return state.filter((toast) => toast.id !== action.id);
    case 'UPDATE':
      return state.map((toast) => (toast.id === action.id ? { ...toast, ...action.next } : toast));
    case 'CLEAR':
      return [];
    default:
      return state;
  }
};

const SonarItem = ({ id, title, description, status = 'info', isClosable = true, onClose, variant = 'soft', icon }) => {
  const statusConfig = statusColors[status] || statusColors.info;
  const accentColor = statusConfig.bg;
  const iconBg = statusConfig.iconBg || statusConfig.softBg || 'gray.50';
  const softText = statusConfig.text || 'gray.800';
  const subtleText = statusConfig.subtleText || 'gray.700';
  const solidText = statusConfig.solidText || 'white';
  const isSolid = variant === 'solid';
  const primaryTextColor = isSolid ? solidText : softText;
  const secondaryTextColor = isSolid ? solidText : subtleText;

  const variantStyles = {
    soft: {
      bg: statusConfig.softBg || 'white',
      border: '1px solid',
      borderColor: statusConfig.softBorder || accentColor,
      boxShadow: 'md',
      color: softText,
    },
    solid: {
      bg: accentColor,
      color: solidText,
      boxShadow: 'lg',
    },
    outline: {
      bg: statusConfig.softBg || 'white',
      border: '1px solid',
      borderColor: accentColor,
      color: softText,
    },
  };

  return (
    <Flex
      role="alert"
      alignItems="flex-start"
      gap={3}
      p={3}
      mb={3}
      borderRadius="lg"
      minW="280px"
      maxW="520px"
      position="relative"
      animation="sonar-fade-in 0.2s ease-out"
      {...(variantStyles[variant] || variantStyles.soft)}
    >
      <Box w="4px" borderRadius="full" bg={accentColor} flexShrink={0} />

      <Flex alignItems="center" justifyContent="center" w={6} h={6} borderRadius="full" bg={iconBg} color={isSolid ? solidText : accentColor} fontSize="sm" flexShrink={0}>
        {icon || statusConfig.icon}
      </Flex>

      <Box flex="1" minW={0}>
        {title && (
          <Text fontWeight="semibold" mb={description ? 1 : 0} noOfLines={2} color={primaryTextColor}>
            {title}
          </Text>
        )}
        {description && (
          <Text fontSize="sm" color={secondaryTextColor} lineHeight="tall" noOfLines={3}>
            {description}
          </Text>
        )}
      </Box>

      {isClosable && (
        <Box
          as="button"
          onClick={() => onClose(id)}
          aria-label="Close notification"
          ml={2}
          px={1}
          py={1}
          borderRadius="md"
          bg="transparent"
          color="gray.500"
          _hover={{ bg: 'gray.100', color: 'gray.700' }}
        >
          ✕
        </Box>
      )}
    </Flex>
  );
};

const SonarContainer = ({ toasts, onClose }) => {
  if (typeof document === 'undefined') return null;

  const grouped = toasts.reduce((acc, toast) => {
    const pos = toast.position || 'top-right';
    if (!acc[pos]) acc[pos] = [];
    acc[pos].push(toast);
    return acc;
  }, {});

  return createPortal(
    <>
      {Object.entries(grouped).map(([position, positionToasts]) => (
        <Box
          key={position}
          position="fixed"
          zIndex="toast"
          pointerEvents="none"
          display="flex"
          flexDirection={position.includes('bottom') ? 'column-reverse' : 'column'}
          {...positionStyles[position]}
        >
          {positionToasts.map((toast) => (
            <Box key={toast.id} pointerEvents="auto">
              <SonarItem {...toast} onClose={onClose} />
            </Box>
          ))}
        </Box>
      ))}
      <style>{`
        @keyframes sonar-fade-in {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>,
    document.body
  );
};

export const SonarProvider = ({ children }) => {
  const [toasts, dispatch] = useReducer(reducer, []);
  const timersRef = useRef(new Map());
  const stateRef = useRef(toasts);

  useEffect(() => {
    stateRef.current = toasts;
  }, [toasts]);

  const clearTimer = useCallback((id) => {
    const timers = timersRef.current;
    const timerId = timers.get(id);
    if (timerId) {
      clearTimeout(timerId);
      timers.delete(id);
    }
  }, []);

  const close = useCallback(
    (id) => {
      clearTimer(id);
      dispatch({ type: 'REMOVE', id });
    },
    [clearTimer]
  );

  const closeAll = useCallback(() => {
    timersRef.current.forEach((timerId) => clearTimeout(timerId));
    timersRef.current.clear();
    dispatch({ type: 'CLEAR' });
  }, []);

  const scheduleDismiss = useCallback(
    (toast) => {
      if (toast.duration === null || toast.duration === Infinity) return;
      clearTimer(toast.id);
      const timerId = setTimeout(() => close(toast.id), toast.duration);
      timersRef.current.set(toast.id, timerId);
    },
    [clearTimer, close]
  );

  const add = useCallback(
    (options = {}) => {
      const id = options.id || `sonar-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const toast = { ...defaultToastOptions, ...options, id };
      dispatch({ type: 'ADD', toast });
      scheduleDismiss(toast);
      return id;
    },
    [scheduleDismiss]
  );

  const update = useCallback(
    (id, next = {}) => {
      const existing = stateRef.current.find((toast) => toast.id === id);
      if (!existing) return;

      const merged = { ...existing, ...next };
      dispatch({ type: 'UPDATE', id, next });

      if (Object.prototype.hasOwnProperty.call(next, 'duration')) {
        if (merged.duration === null || merged.duration === Infinity) {
          clearTimer(id);
        } else {
          scheduleDismiss(merged);
        }
      }
    },
    [clearTimer, scheduleDismiss]
  );

  const isActive = useCallback((id) => stateRef.current.some((toast) => toast.id === id), []);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((timerId) => clearTimeout(timerId));
      timersRef.current.clear();
    };
  }, []);

  const sonar = useMemo(() => {
    const api = add;
    api.success = (opts) => add({ status: 'success', ...opts });
    api.error = (opts) => add({ status: 'error', ...opts });
    api.warning = (opts) => add({ status: 'warning', ...opts });
    api.info = (opts) => add({ status: 'info', ...opts });
    api.close = close;
    api.closeAll = closeAll;
    api.update = update;
    api.isActive = isActive;
    return api;
  }, [add, close, closeAll, update, isActive]);

  return (
    <SonarContext.Provider value={sonar}>
      {children}
      <SonarContainer toasts={toasts} onClose={close} />
    </SonarContext.Provider>
  );
};

export const useSonar = () => {
  const context = useContext(SonarContext);

  if (!context) {
    const fallback = () => null;
    fallback.success = fallback;
    fallback.error = fallback;
    fallback.warning = fallback;
    fallback.info = fallback;
    fallback.close = () => {};
    fallback.closeAll = () => {};
    fallback.update = () => {};
    fallback.isActive = () => false;
    return fallback;
  }

  return context;
};

export default useSonar;
